import React, { useState, useEffect, useRef } from 'react';
import { documentsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Upload, FileText, Eye, Trash2, Loader2, Camera } from 'lucide-react';
import { DocumentPreviewDialog } from './DocumentPreviewDialog';
import { PhotoLightbox } from './PhotoLightbox';
import { PhotoGallery } from './PhotoGallery';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';
import { openSignedFile } from '../lib/openFile';

const formatBytes = (b) => {
    if (!b) return '0 B';
    const k = 1024, sizes = ['B', 'KB', 'MB'];
    const i = Math.min(Math.floor(Math.log(b) / Math.log(k)), 2);
    return `${parseFloat((b / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const OpportunityDocuments = ({ opportunityId, onChanged }) => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [viewingId, setViewingId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const photos = docs.filter(d => d.thumb_url);
    const inputRef = useRef(null);
    const cameraRef = useRef(null);

    useEffect(() => {
        if (opportunityId) loadDocs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opportunityId]);

    const loadDocs = async () => {
        setLoading(true);
        try {
            const res = await documentsAPI.list(opportunityId);
            setDocs(res.data);
        } catch { /* noop */ } finally { setLoading(false); }
    };

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        try {
            await documentsAPI.upload(opportunityId, file);
            toast.success('Documento caricato');
            loadDocs(); onChanged?.();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel caricamento'));
        } finally { setUploading(false); }
    };

    const handleView = async (doc) => {
        const photoIdx = photos.findIndex(p => p.id === doc.id);
        if (photoIdx >= 0) { setLightbox(photoIdx); return; }
        setViewingId(doc.id);
        try {
            if (/^application\/pdf/.test(doc.content_type || '')) {
                setPreview({ doc, url: null });
                const { data } = await documentsAPI.link(doc.id);
                setPreview({ doc, url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` });
                return;
            }
            await openSignedFile(() => documentsAPI.link(doc.id), doc.content_type);
        } catch (error) {
            setPreview(null);
            toast.error(apiErrorMessage(error, 'Errore nell\'apertura'));
        } finally { setViewingId(null); }
    };

    const handleSetCover = async (photo) => {
        try {
            await documentsAPI.setCover(photo.id);
            setDocs(docs.map(d => (d.thumb_url ? { ...d, is_cover: d.id === photo.id } : d)));
            toast.success('Copertina impostata');
            onChanged?.();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nell\'impostare la copertina'));
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(`Eliminare "${doc.filename}"?`)) return;
        try {
            await documentsAPI.delete(doc.id);
            toast.success('Documento eliminato');
            loadDocs(); onChanged?.();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nell\'eliminazione'));
        }
    };

    if (!opportunityId) {
        return (
            <div className="border-t pt-4" data-testid="documents-section">
                <Label>Documenti</Label>
                <p className="text-xs text-muted-foreground mt-1">
                    Salva prima l'offerta, poi potrai allegare documenti (PDF, Excel, JPG...).
                </p>
            </div>
        );
    }

    return (
        <div className="border-t pt-4 space-y-3" data-testid="documents-section">
            <div className="flex items-center justify-between">
                <Label>Documenti allegati</Label>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,.xls,.xlsx,.doc,.docx,.csv,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={onPick}
                    data-testid="document-file-input"
                />
                <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={onPick}
                    data-testid="document-camera-input"
                />
                <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="document-camera-btn">
                        <Camera size={14} className="mr-2" /> Foto
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="document-upload-btn">
                        {uploading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
                        Carica
                    </Button>
                </div>
            </div>
            <DocumentPreviewDialog doc={preview?.doc} url={preview?.url} onClose={() => setPreview(null)} />
            <PhotoLightbox photos={photos} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} onSetCover={handleSetCover} />
            {photos.length > 0 && <PhotoGallery photos={photos} onOpen={setLightbox} />}
            {loading ? (
                <div className="flex justify-center py-3"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>
            ) : docs.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nessun documento. Ammessi: PDF, Excel, Word, CSV, JPG, PNG (max 25 MB).</p>
            ) : (
                <div className="space-y-2">
                    {docs.map((d) => (
                        <div key={d.id} className="flex items-center gap-2 p-2 border rounded-sm text-sm" data-testid={`document-row-${d.id}`}>
                            <FileText size={16} className="text-primary shrink-0" />
                            <span className="flex-1 min-w-0 truncate">{d.filename}</span>
                            <span className="text-xs text-muted-foreground shrink-0">{formatBytes(d.size)}</span>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleView(d)} disabled={viewingId === d.id} data-testid={`document-view-${d.id}`} title="Visualizza / Scarica">
                                {viewingId === d.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(d)} data-testid={`document-delete-${d.id}`} title="Elimina">
                                <Trash2 size={14} className="text-destructive" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const Label = ({ children }) => (
    <span className="text-xs uppercase tracking-wider font-medium">{children}</span>
);

export default OpportunityDocuments;
